import { GenericColumn } from "../components/KanbanBoard/utils/tableColumns";

/**
 * Generate and download CSV file from data
 * @param data Array of data objects to export
 * @param visibleColumns Map of column keys to visibility boolean
 * @param columns Array of column definitions
 * @param filename Filename for the downloaded CSV
 * @returns Promise that resolves when download is complete
 */
export const downloadCSV = <T extends { id: string | number }>(
  data: T[],
  visibleColumns: Record<string, boolean>,
  columns: GenericColumn<T>[],
  filename: string
): Promise<void> => {
  return new Promise((resolve) => {
    // Get the visible columns that should be included in the CSV
    const visibleColumnKeys = Object.entries(visibleColumns)
      .filter(([_, isVisible]) => isVisible)
      .map(([key]) => key)
      .filter(key => key !== 'actions' && key !== 'select'); // Exclude actions and select columns

    // Filter columns to only include visible ones that have toCSV method
    const csvColumns = columns.filter(col =>
      visibleColumnKeys.includes(col.key.toString()) && col.toCSV
    );

    // Create CSV headers based on visible columns
    const headers = csvColumns.map(col => col.label);

    // Convert data to CSV rows using toCSV methods
    const rows = data.map(item =>
      csvColumns.map(col => col.toCSV ? col.toCSV(item) : '-')
    );

    // Combine headers and rows into CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      resolve();
    }, 100);
  });
};
